<template>
  <v-app-bar
    :key="activeDashboardIndex"
    app
    :style="{backgroundColor: `${navBarColor} !important`}"
    extension-height="55"
    elevate-on-scroll
  >
    <DashLogo
      v-if="logo"
      :logo="logo"
      class="pl-2"
    />
    <v-spacer />

    <TrialStudio
      v-if="isEditor"
      :account-id="accountId"
      :universe-id="universeId"
      :user="user"
      is-studio
      class="trial-studio"
    />

    <div v-if="isStaging">
      <StIcon
        v-if="isMobile"
        v-tooltip="isStagingTooltip"
        icon-name="alert"
        color="warning"
        class="mr-3"
      />
      <v-chip
        v-else
        v-tooltip="isStagingTooltip"
        pill
        color="warning"
        text-color="black"
        class="mr-5"
      >
        <StIcon icon-name="alert" />
        {{ isStagingTitleChip }}
      </v-chip>
    </div>

    <div
      v-if="showDashboardSelector"
      class="dashboard-select-container"
    >
      <v-select
        v-model="dashboard"
        :searchable="true"
        :items="dashboardsFormatted"
        :placeholder="dashboardSelectPlaceholder"
        class="dashboard-select-container__select"
        solo
      />
    </div>

    <HelpButton />

    <UserMenu />

    <template v-slot:extension>
      <GlobalFilter />
    </template>
  </v-app-bar>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import UserMenu from '@/components/UserMenu';
import DashLogo from '@/components/dashboard/DashLogo';
import StIcon from '@stilingue/st-icon';
import TrialStudio from '@stilingue/st-trial-studio';
import GlobalFilter from '@/components/filter/GlobalFilter';
import HelpButton from '@/components/HelpButton';

const bablic = window.bablic;

const pt = {
  label: 'Português',
  formatted: 'Português (PT)',
  initials: 'PT',
  bablic_initials: bablic.languages.get('pt').key,
};

export default {
  name: 'DashTopBar',
  components: {
    UserMenu,
    DashLogo,
    StIcon,
    TrialStudio,
    GlobalFilter,
    HelpButton,
  },
  data() {
    return {
      dashboardSelectlabel: 'Dashboard',
      dashboardSelectPlaceholder: 'Selecione um dashboard',
      noTitleDasboard: 'Dashboard sem titulo',
      isStagingTooltip: 'Essa pesquisa pertence ao ambiente de teste (staging)',
      isStagingTitleChip: 'Staging',
    };
  },
  computed: {
    ...mapState('dashboard', [
      'activeDashboardIndex',
      'dashboards',
      'isStaging',
    ]),
    ...mapState('user', [
      'user',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
      'activeLanguage',
    ]),
    ...mapGetters('user', [
      'isEditor',
      'isInternational',
    ]),
    navBarColor() {
      if (this.activeDashboard && this.activeDashboard.data && this.activeDashboard.data.color) {
        return this.activeDashboard.data.color;
      }
      return null;
    },
    logo() {
      if (this.activeDashboard && this.activeDashboard.data && this.activeDashboard.data.logo) {
        return this.activeDashboard.data.logo;
      }
      return null;
    },
    dashboard: {
      get() {
        return this.activeDashboardIndex;
      },
      set(newDashboard) {
        this.setActiveDashboard(newDashboard);
      },
    },
    dashboardsFormatted() {
      return this.dashboards.map((dashboard, index) => {
        return {
          text: dashboard.data.title || this.noTitleDasboard,
          value: index,
        };
      });
    },
    showDashboardSelector() {
      return this.dashboardsFormatted.length > 1;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    showLanguageSelector() {
      return this.isInternational;
    },
  },
  updated() {
    if (this.activeLanguage) {
      this.saveLanguage(pt);
      if (this.$route.query.locale) {
        bablic.redirectTo(pt.bablic_initials);
      }
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'setActiveDashboard',
      'saveLanguage',
    ]),
  },
};
</script>

<style scoped lang="scss">
  .v-app-bar {
    z-index: 8 !important;
    max-height: 112px;
  }

  .dashboard-select-container {
    display: flex;
    justify-content: flex-end;
    min-width: 160px;

    &__select {
      position: relative;
      min-width: auto !important;
      height: 24px !important;
      padding: 0  var(--ds-spacing-stack-quark) 0 var(--ds-spacing-stack-sm) !important;
      font-size: var(--ds-font-size-xs);
      line-height: var(--ds-font-line-height-dense);
      text-align: left;
      text-transform: none;
    }
  }

  .trial-studio {
    z-index: 1;
  }

  @media only screen and (max-width: 640px) {
    .dashboard-select-container {
      width: 165px;
      padding-right: 16px;
    }
  }

  ::v-deep {
    .v-select__selection.v-select__selection--comma {
      line-height: 1.4;
    }

    .v-toolbar__content {
      border-bottom: 1px solid var(--ds-color-gray-3);
      height: 56px !important;
      z-index: 2;
    }

    .v-toolbar__extension {
      padding: 0;
      position: static;
    }

    .v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
      box-shadow: 0;
      height: 20px;
    }

    .v-text-field__details {
      display: none;
    }

    .v-text-field--enclosed:not(.v-input__prepend-outer) > .v-input__control > .v-input__slot {
      background-color: transparent !important;
      padding: 0 0 0 4px;
      box-shadow: none;

      &:hover {
        background-color: var(--ds-color-background) !important;
      }
    }

    .v-text-field.v-text-field--solo .v-input__control {
      min-height: 25.5px;
    }

    .v-input__icon--append > .v-icon {
      color: var(--ds-color-icon-2);
    }

    .v-select {
      &__selections input {
        display: none;
      }

      &__selection {
        max-width: none;
      }
    }
  }
</style>
